import { createApiRef, useApi, createRouteRef, createPlugin, createApiFactory, discoveryApiRef, fetchApiRef, createRoutableExtension } from '@backstage/core-plugin-api';
import { parseMadrWithFrontmatter, getAdrLocationUrl } from '@backstage/plugin-adr-common';
import React, { useMemo } from 'react';
import { InfoCard, Progress, WarningPanel, MarkdownContent } from '@backstage/core-components';
import { scmIntegrationsApiRef } from '@backstage/integration-react';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import { createSearchResultListItemExtension } from '@backstage/plugin-search-react';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
const readEndpoint = "file";
const listEndpoint = "list";
class AdrClient {
  constructor(options) {
    __publicField(this, "discoveryApi");
    __publicField(this, "fetchApi");
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }
  async fetchAdrApi(endpoint, fileUrl) {
    const baseUrl = await this.discoveryApi.getBaseUrl("adr");
    const targetUrl = `${baseUrl}/${endpoint}?url=${encodeURIComponent(
      fileUrl
    )}`;
    const result = await this.fetchApi.fetch(targetUrl);
    const data = await result.json();
    if (!result.ok) {
      throw new Error(`${data.message}`);
    }
    return data;
  }
  async listAdrs(url) {
    return this.fetchAdrApi(listEndpoint, url);
  }
  async readAdr(url) {
    return this.fetchAdrApi(readEndpoint, url);
  }
}

const adrApiRef = createApiRef({
  id: "plugin.adr.api"
});

const adrDecoratorFactories = Object.freeze({
  /**
   * Rewrites relative Markdown links as absolute links.
   */
  createRewriteRelativeLinksDecorator() {
    return ({ baseUrl, content }) => ({
      content: content.replace(
        /\[([^\[\]]*)\]\((?!https?:\/\/)(.*?)(\.md)\)/gim,
        `[$1](${baseUrl}/$2$3)`
      )
    });
  },
  /**
   * Rewrites relative Markdown embeds using absolute URLs.
   */
  createRewriteRelativeEmbedsDecorator() {
    return ({ baseUrl, content }) => ({
      content: content.replace(
        /!\[([^\[\]]*)\]\((?!https?:\/\/)(.*?)(\.png|\.jpg|\.jpeg|\.gif|\.webp)(.*)\)/gim,
        `![$1](${baseUrl}/$2$3$4)`
      )
    });
  },
  /**
   * Formats YAML front-matter into a table format (if any exists in the markdown document)
   */
  createFrontMatterFormatterDecorator() {
    return ({ content }) => {
      const parsedFrontmatter = parseMadrWithFrontmatter(content);
      let table = "";
      const attrs = parsedFrontmatter.attributes;
      if (Object.keys(attrs).length > 0) {
        const stripNewLines = (val) => String(val).replaceAll("\n", "<br/>");
        const row = (vals) => `|${vals.join("|")}|
`;
        table = `${row(Object.keys(attrs))}`;
        table += `${row(Object.keys(attrs).map(() => "---"))}`;
        table += `${row(Object.values(attrs).map(stripNewLines))}

`;
      }
      return { content: table + parsedFrontmatter.content };
    };
  }
});

const AdrReader = (props) => {
  const { adr, decorators } = props;
  const { entity } = useEntity();
  const scmIntegrations = useApi(scmIntegrationsApiRef);
  const adrApi = useApi(adrApiRef);
  const adrLocationUrl = getAdrLocationUrl(entity, scmIntegrations);
  const url = `${adrLocationUrl.replace(/\/$/, "")}/${adr}`;
  const { value, loading, error } = useAsync(
    async () => adrApi.readAdr(url),
    [url]
  );
  const adrContent = useMemo(() => {
    if (!(value == null ? void 0 : value.data)) {
      return "";
    }
    const adrDecorators = decorators != null ? decorators : [
      adrDecoratorFactories.createRewriteRelativeLinksDecorator(),
      adrDecoratorFactories.createRewriteRelativeEmbedsDecorator(),
      adrDecoratorFactories.createFrontMatterFormatterDecorator()
    ];
    return adrDecorators.reduce(
      (content, decorator) => decorator({ baseUrl: adrLocationUrl, content }).content,
      value.data
    );
  }, [adrLocationUrl, decorators, value]);
  return /* @__PURE__ */ React.createElement(InfoCard, null, loading && /* @__PURE__ */ React.createElement(Progress, null), !loading && error && /* @__PURE__ */ React.createElement(WarningPanel, { title: "Failed to fetch ADR", message: error == null ? void 0 : error.message }), !loading && !error && (value == null ? void 0 : value.data) && /* @__PURE__ */ React.createElement(MarkdownContent, { content: adrContent, linkTarget: "_blank" }));
};
AdrReader.decorators = adrDecoratorFactories;

const rootRouteRef = createRouteRef({
  id: "adr"
});

const adrPlugin = createPlugin({
  id: "adr",
  apis: [
    createApiFactory({
      api: adrApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef
      },
      factory({ discoveryApi, fetchApi }) {
        return new AdrClient({ discoveryApi, fetchApi });
      }
    })
  ],
  routes: {
    root: rootRouteRef
  }
});
const EntityAdrContent = adrPlugin.provide(
  createRoutableExtension({
    name: "EntityAdrContent",
    component: () => import('./index-748a9bf7.esm.js').then((m) => m.EntityAdrContent),
    mountPoint: rootRouteRef
  })
);
const AdrSearchResultListItem = adrPlugin.provide(
  createSearchResultListItemExtension({
    name: "AdrSearchResultListItem",
    component: () => import('./AdrSearchResultListItem-6feae4dd.esm.js').then(
      (m) => m.AdrSearchResultListItem
    ),
    predicate: (result) => result.type === "adr"
  })
);

export { AdrReader as A, EntityAdrContent as E, adrApiRef as a, AdrClient as b, adrPlugin as c, AdrSearchResultListItem as d, rootRouteRef as r };
//# sourceMappingURL=index-8e101972.esm.js.map
